import type {
  GenerationNotificationDiagnostic,
  GenerationNotificationLegacyReconciliationDto,
  GenerationNotificationRecoveryDto,
  GenerationNotificationStatus,
} from "@dutyon/apis";

export const notificationStates: Record<GenerationNotificationStatus, string> = {
  UNKNOWN: "발송 확인 필요",
  BLOCKED: "차단됨",
  RETRY: "재시도 대기",
  PENDING: "발송 대기",
  LEASED: "발송 확인 중",
  SENT: "발송 확인됨",
};
export const notificationApps = {
  dashboard: "대시보드",
  backoffice: "백오피스",
  pro: "Pro",
  mini: "Mini",
  manual: "수동 요청",
  unknown: "제품 확인 필요",
};
export const notificationKinds: Record<string, string> = {
  REQUEST: "요청 접수",
  ACCEPTED: "요청 접수",
  SUCCEEDED: "생성 성공",
  FAILED: "생성 실패",
  COMPLETED: "업무 완료",
  CANCELLED: "취소",
  EXPIRED: "만료",
  UNKNOWN: "유형 확인 필요",
};
export function notificationTime(value: string | null) {
  return value
    ? new Date(value).toLocaleString("ko-KR", { timeZone: "Asia/Seoul", hour12: false })
    : "기록 없음";
}
const errorReasons: Record<string, string> = {
  SLACK_LEGACY_PARENT_UNCONFIRMED: "기존 요청 메시지를 확인하지 못했습니다.",
  SLACK_AUTHENTICATION_REQUIRED: "Bot 인증을 확인해 주세요.",
  SLACK_CHANNEL_UNAVAILABLE: "원래 채널의 접근 상태를 확인해 주세요.",
  SLACK_PERMISSION_REQUIRED: "Bot 발송 권한을 확인해 주세요.",
  SLACK_PAYLOAD_REJECTED: "메시지 형식이 거절되었습니다.",
  SLACK_THREAD_UNAVAILABLE: "원래 스레드를 확인해 주세요.",
  SLACK_RETRY_AFTER_INVALID: "제공자가 안내한 대기 시간을 확인하지 못했습니다.",
  SLACK_CONFIGURATION_OR_PAYLOAD_INVALID: "발송 설정과 메시지 구성을 확인해 주세요.",
  SLACK_SNAPSHOT_INVALID: "접수 요약을 검증하지 못했습니다.",
  SLACK_WIRE_PROOF_UNAVAILABLE: "발송 전 검증 정보를 저장하지 못해 차단했습니다.",
  SLACK_RATE_LIMITED: "제공자가 안내한 대기 시간 이후 처리합니다.",
  SLACK_LEASE_EXPIRED: "전송 확인 시간이 지났습니다.",
  SLACK_HTTP_UNCERTAIN: "발송 여부를 확인하지 못했습니다.",
  SLACK_ACK_MISMATCH: "발송 확인 대상이 일치하지 않습니다.",
  SLACK_ACK_UNCONFIRMED: "발송 확인을 검증하지 못했습니다.",
  SLACK_DELIVERY_TIMEOUT: "발송 확인 응답 시간이 지났습니다.",
  SLACK_TRANSPORT_UNCERTAIN: "연결 오류로 발송 여부를 확인하지 못했습니다.",
};
export function notificationError(code: string) {
  return Object.keys(errorReasons).includes(code)
    ? errorReasons[code]
    : "원인을 별도 검토해 주세요.";
}
export function notificationReason(row: GenerationNotificationDiagnostic) {
  if (row.lastError?.code === "SLACK_LEGACY_PARENT_UNCONFIRMED")
    return "원래 업무에 저장된 발송 기록을 다시 대조할 수 있습니다. 기록이 없거나 일치하지 않으면 별도 검토가 필요합니다.";
  if (row.waitingForParent) return "원래 요청 메시지의 발송 확인을 기다리고 있습니다.";
  if (row.status === "UNKNOWN")
    return row.candidateLookupAvailable
      ? "정확한 후보 메시지를 조회해 발송 여부를 확인할 수 있습니다."
      : "당시 전송 증거가 없어 이 화면에서 후보를 확인할 수 없습니다. 원장과 원래 메시지를 별도 검토해 주세요.";
  if (row.status === "RETRY") return "기록된 대기 시간 이후 전달기가 처리합니다.";
  if (row.status === "BLOCKED")
    return "발송 설정·Bot 권한·채널과 기록된 실패 원인을 운영 절차에 따라 확인해 주세요.";
  if (row.status === "LEASED") return "전달기가 발송 결과를 확인하고 있습니다.";
  if (row.status === "SENT") return "원래 메시지의 발송 확인이 기록되어 있습니다.";
  return "전달기의 처리를 기다리고 있습니다.";
}
export function legacyReceiptAvailable(row: GenerationNotificationDiagnostic) {
  return (
    row.sourceKind === "MANUAL" &&
    row.role === "PARENT" &&
    row.slot === "ACCEPTED" &&
    row.status === "BLOCKED" &&
    row.attemptCount === 0 &&
    row.lastError?.code === "SLACK_LEGACY_PARENT_UNCONFIRMED"
  );
}
export function legacyReceiptMessage(result: GenerationNotificationLegacyReconciliationDto) {
  if (result.status === "reconciled")
    return "업무에 저장된 원래 발송 기록을 연결했습니다. 목록을 다시 조회해 주세요.";
  if (result.status === "already_confirmed")
    return "원래 발송 기록이 이미 연결되어 있습니다. 목록을 다시 조회해 주세요.";
  if (result.code === "SLACK_LEGACY_RECEIPT_PENDING")
    return "업무에 확정된 발송 기록이 아직 없습니다. 원래 발송 결과와 기록을 별도 확인해 주세요.";
  if (result.code === "SLACK_LEGACY_CHANNEL_MISMATCH")
    return "업무에 저장된 채널과 원래 알림 채널이 다릅니다. 기존 연결을 유지하고 별도 검토해 주세요.";
  return "저장된 발송 기록이 기존 연결과 일치하는지 확인하지 못했습니다. 기존 상태를 유지하고 별도 검토해 주세요.";
}
export function recoveryMessage(result: GenerationNotificationRecoveryDto) {
  if (result.status === "recovered")
    return "후보 메시지가 일치해 원래 발송 확인을 기록했습니다. 목록을 다시 조회해 주세요.";
  if (result.status === "already_confirmed")
    return "같은 메시지의 발송 확인이 이미 기록되어 있습니다. 목록을 다시 조회해 주세요.";
  if (result.code === "SLACK_HISTORY_CREDENTIAL_CHANGED")
    return "현재 Bot 자격증명이 당시와 달라 확인하지 못했습니다. 당시 설정을 운영 절차에서 확인해 주세요.";
  if (result.code === "SLACK_HISTORY_PERMISSION_REQUIRED")
    return "Bot의 조회 권한을 확인해 주세요. 알림 상태는 그대로 유지됩니다.";
  if (result.code === "SLACK_HISTORY_RATE_LIMITED")
    return "Slack 조회 대기 중입니다. 안내된 시간이 지난 뒤 목록과 후보를 다시 확인해 주세요.";
  return "후보 메시지를 확정하지 못했습니다. 알림 상태와 원래 메시지를 다시 확인해 주세요. 이 결과를 근거로 새 메시지를 발송하지 않습니다.";
}
